import { supabase } from "./supabase.js";

// ────────────────────────────────────────────────────────────────────
// Queue + stats (dashboard)
// ────────────────────────────────────────────────────────────────────

export async function listAdminQueue({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from("it_cases")
    .select(`
      id, status, concern_tags, concern_memo,
      submitted_at, created_at, plan_tier, ai_model,
      parent_id, child_id,
      child:it_children(id, name, birth_month, avatar_url),
      videos:it_videos(count)
    `)
    .neq("status", "draft")
    .order("submitted_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((c) => ({
    ...c,
    video_count: c.videos?.[0]?.count ?? 0,
  }));
}

export async function getAdminStats() {
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - 7);

  const [newToday, inProgress, reviewing, doneThisWeek] = await Promise.all([
    supabase.from("it_cases").select("id", { count: "exact", head: true })
      .eq("status", "received").gte("submitted_at", dayStart.toISOString()),
    supabase.from("it_cases").select("id", { count: "exact", head: true })
      .in("status", ["analyzing", "research"]),
    supabase.from("it_cases").select("id", { count: "exact", head: true })
      .eq("status", "reviewing"),
    supabase.from("it_cases").select("id", { count: "exact", head: true })
      .eq("status", "done").gte("sent_at", weekStart.toISOString()),
  ]);

  return {
    newToday: newToday.count ?? 0,
    inProgress: inProgress.count ?? 0,
    reviewing: reviewing.count ?? 0,
    doneThisWeek: doneThisWeek.count ?? 0,
  };
}

// ────────────────────────────────────────────────────────────────────
// Report archive — finalized 케이스(status='done') 다시 보기
// ────────────────────────────────────────────────────────────────────

// 발행 완료된 케이스 목록. sent_at 최신순. 검색어는 클라이언트 필터로
// 처리한다 — Postgrest의 or() 문법이 colon/괄호에 약한데 운영 화면에서
// 케이스 수가 폭주할 일은 적어 후처리 비용이 충분히 싸다.
export async function listAdminArchive({ limit = 50, offset = 0, search = "" } = {}) {
  const { data, error, count } = await supabase
    .from("it_cases")
    .select(`
      id, status, concern_tags, sent_at, plan_tier, ai_model,
      parent_id, child_id,
      parent:it_users!it_cases_parent_id_fkey(id, email, display_name),
      child:it_children(id, name, birth_month, avatar_url),
      videos:it_videos(count)
    `, { count: "exact" })
    .eq("status", "done")
    .order("sent_at", { ascending: false, nullsFirst: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;

  let rows = (data ?? []).map((c) => {
    const parent = Array.isArray(c.parent) ? c.parent[0] : c.parent;
    const child = Array.isArray(c.child) ? c.child[0] : c.child;
    return { ...c, parent, child, video_count: c.videos?.[0]?.count ?? 0 };
  });

  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter((r) => {
      const hay = [
        r.child?.name,
        r.parent?.email,
        r.parent?.display_name,
        ...(r.concern_tags ?? []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }

  return { rows, total: count ?? rows.length };
}

// ────────────────────────────────────────────────────────────────────
// Stats overview — 운영 대시보드용 집계
// ────────────────────────────────────────────────────────────────────

// 이번 달 + 최근 30일 시계열 + 플랜 분포 + 증상 top + AI 토큰을 한 번에 모은다.
// 데이터 양이 크지 않을 때까지(수만 건 이하) 전체 row를 가져와 클라이언트 집계.
// 더 커지면 RPC/머티리얼라이즈드 뷰로 옮길 것.
export async function getAdminStatsOverview() {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const last30Start = new Date(now);
  last30Start.setDate(last30Start.getDate() - 29); // 오늘 포함 30일
  last30Start.setHours(0, 0, 0, 0);

  const [
    casesRes,
    usersRes,
    purchasesRes,
    researchRes,
  ] = await Promise.all([
    supabase
      .from("it_cases")
      .select("id, status, concern_tags, submitted_at, sent_at, created_at, plan_tier")
      .gte("created_at", last30Start.toISOString())
      .order("created_at", { ascending: false })
      .limit(2000),
    supabase
      .from("it_users")
      .select("id, role, created_at")
      .eq("role", "parent")
      .gte("created_at", last30Start.toISOString())
      .order("created_at", { ascending: false })
      .limit(2000),
    supabase
      .from("it_purchases")
      .select("id, amount_won, plan_type, status, paid_at, created_at")
      .gte("created_at", last30Start.toISOString())
      .order("created_at", { ascending: false })
      .limit(2000),
    supabase
      .from("it_research")
      .select("id, tokens_input, tokens_output, model_used, completed_at, created_at")
      .gte("created_at", last30Start.toISOString())
      .order("created_at", { ascending: false })
      .limit(2000),
  ]);

  if (casesRes.error) throw casesRes.error;
  if (usersRes.error) throw usersRes.error;
  if (purchasesRes.error) throw purchasesRes.error;
  if (researchRes.error) throw researchRes.error;

  const cases = casesRes.data ?? [];
  const newUsers = usersRes.data ?? [];
  const purchases = purchasesRes.data ?? [];
  const research = researchRes.data ?? [];

  const inThisMonth = (iso) => iso && new Date(iso).getTime() >= monthStart.getTime();

  // KPI: 이번 달 기준
  const monthNewCases = cases.filter((c) => inThisMonth(c.created_at)).length;
  const monthDoneCases = cases.filter((c) => c.status === "done" && inThisMonth(c.sent_at)).length;
  const monthNewUsers = newUsers.filter((u) => inThisMonth(u.created_at)).length;
  const monthRevenue = purchases
    .filter((p) => p.status === "paid" && inThisMonth(p.paid_at))
    .reduce((acc, p) => acc + (p.amount_won ?? 0), 0);

  // 평균 발행 시간 (이번 달 발행 케이스)
  const monthDoneWithTimes = cases.filter((c) =>
    c.status === "done" && inThisMonth(c.sent_at) && (c.submitted_at || c.created_at)
  );
  const avgPublishMs = monthDoneWithTimes.length
    ? monthDoneWithTimes.reduce((acc, c) => {
        const start = new Date(c.submitted_at ?? c.created_at).getTime();
        const end = new Date(c.sent_at).getTime();
        return acc + Math.max(0, end - start);
      }, 0) / monthDoneWithTimes.length
    : null;

  // 최근 30일 일별 시계열
  const days = [];
  for (let i = 0; i < 30; i++) {
    const d = new Date(last30Start);
    d.setDate(last30Start.getDate() + i);
    const key = d.toISOString().slice(0, 10);
    days.push({ date: key, received: 0, done: 0, revenue: 0 });
  }
  const dayIndex = Object.fromEntries(days.map((d, i) => [d.date, i]));
  cases.forEach((c) => {
    const recK = c.submitted_at ?? c.created_at;
    if (recK) {
      const k = recK.slice(0, 10);
      if (k in dayIndex) days[dayIndex[k]].received += 1;
    }
    if (c.status === "done" && c.sent_at) {
      const k = c.sent_at.slice(0, 10);
      if (k in dayIndex) days[dayIndex[k]].done += 1;
    }
  });
  purchases.forEach((p) => {
    if (p.status === "paid" && p.paid_at) {
      const k = p.paid_at.slice(0, 10);
      if (k in dayIndex) days[dayIndex[k]].revenue += p.amount_won ?? 0;
    }
  });

  // 플랜별 매출 (최근 30일 paid)
  const planRevenue = {};
  purchases
    .filter((p) => p.status === "paid")
    .forEach((p) => {
      const key = p.plan_type ?? "unknown";
      planRevenue[key] = (planRevenue[key] ?? 0) + (p.amount_won ?? 0);
    });

  // 증상 태그 top (최근 30일 케이스)
  const tagCount = {};
  cases.forEach((c) => {
    (c.concern_tags ?? []).forEach((t) => {
      if (!t) return;
      tagCount[t] = (tagCount[t] ?? 0) + 1;
    });
  });
  const topTags = Object.entries(tagCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([tag, count]) => ({ tag, count }));

  // AI 토큰 합계 (최근 30일 + 이번 달)
  const monthResearch = research.filter((r) =>
    inThisMonth(r.completed_at ?? r.created_at),
  );
  const monthTokensIn = monthResearch.reduce((acc, r) => acc + (r.tokens_input ?? 0), 0);
  const monthTokensOut = monthResearch.reduce((acc, r) => acc + (r.tokens_output ?? 0), 0);

  // 모델별 토큰 분배 (Opus가 비용이 훨씬 비싸서 분리해 보여줘야 의미 있음)
  const modelTokens = {};
  monthResearch.forEach((r) => {
    const m = r.model_used ?? "unknown";
    if (!modelTokens[m]) modelTokens[m] = { in: 0, out: 0, count: 0 };
    modelTokens[m].in += r.tokens_input ?? 0;
    modelTokens[m].out += r.tokens_output ?? 0;
    modelTokens[m].count += 1;
  });

  return {
    range: {
      monthStart: monthStart.toISOString(),
      last30Start: last30Start.toISOString(),
      now: now.toISOString(),
    },
    kpi: {
      monthNewCases,
      monthDoneCases,
      monthNewUsers,
      monthRevenue,
      avgPublishMs,
      monthTokensIn,
      monthTokensOut,
    },
    daily: days,
    planRevenue,
    topTags,
    modelTokens,
  };
}

// ────────────────────────────────────────────────────────────────────
// Parents — 보호자 목록 + 상세
// ────────────────────────────────────────────────────────────────────

// 보호자 목록. 각 보호자의 자녀 수·총 케이스·발행된 리포트 수·누적 결제 금액·
// 남은 분석권·최근 활동을 한 번의 쿼리로 받아 클라이언트에서 집계한다.
// 검색은 이메일·display_name 기준 클라이언트 필터.
export async function listParents({ limit = 50, offset = 0, search = "" } = {}) {
  const { data, error, count } = await supabase
    .from("it_users")
    .select(`
      id, email, display_name, created_at,
      children:it_children(id),
      cases:it_cases!parent_id(id, status, sent_at, submitted_at, created_at),
      purchases:it_purchases(amount_won, status, paid_at),
      credits:it_credit_packages(credits_remaining, expires_at)
    `, { count: "exact" })
    .eq("role", "parent")
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;

  let rows = (data ?? []).map((u) => {
    const cases = u.cases ?? [];
    const done = cases.filter((c) => c.status === "done");
    const paid = (u.purchases ?? []).filter((p) => p.status === "paid");
    const now = Date.now();
    const credits = (u.credits ?? [])
      .filter((p) => !p.expires_at || new Date(p.expires_at).getTime() > now)
      .reduce((acc, p) => acc + (p.credits_remaining ?? 0), 0);

    // 최근 활동 = 케이스/결제 중 가장 최근 시각
    const candidates = [
      ...cases.map((c) => c.sent_at ?? c.submitted_at ?? c.created_at),
      ...paid.map((p) => p.paid_at),
      u.created_at,
    ].filter(Boolean).map((s) => new Date(s).getTime());
    const lastActivity = candidates.length ? new Date(Math.max(...candidates)).toISOString() : u.created_at;

    return {
      id: u.id,
      email: u.email,
      display_name: u.display_name,
      created_at: u.created_at,
      children_count: (u.children ?? []).length,
      cases_count: cases.length,
      done_count: done.length,
      credits_remaining: credits,
      total_paid: paid.reduce((acc, p) => acc + (p.amount_won ?? 0), 0),
      last_activity: lastActivity,
    };
  });

  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter((r) => {
      return [r.email, r.display_name].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }

  // 최근 활동 desc — 활발한 보호자가 위로
  rows.sort((a, b) => {
    const aT = a.last_activity ? new Date(a.last_activity).getTime() : 0;
    const bT = b.last_activity ? new Date(b.last_activity).getTime() : 0;
    return bT - aT;
  });

  return { rows, total: count ?? rows.length };
}

// 단일 보호자의 상세 정보. 자녀·케이스·결제·알림 + 기본 메타.
export async function getParentDetail(userId) {
  if (!userId) return null;

  const [userRes, childrenRes, casesRes, purchasesRes, creditsRes, notifsRes] = await Promise.all([
    supabase
      .from("it_users")
      .select("id, email, display_name, role, created_at, updated_at")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("it_children")
      .select("id, name, birth_month, avatar_url, created_at")
      .eq("parent_id", userId)
      .order("created_at", { ascending: true }),
    supabase
      .from("it_cases")
      .select(`
        id, status, concern_tags, plan_tier, ai_model,
        submitted_at, sent_at, created_at,
        child:it_children(id, name, birth_month)
      `)
      .eq("parent_id", userId)
      .order("created_at", { ascending: false }),
    supabase
      .from("it_purchases")
      .select("id, amount_won, plan_type, payment_provider, status, paid_at, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false }),
    supabase
      .from("it_credit_packages")
      .select("id, package_type, credits_total, credits_remaining, expires_at, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false }),
    supabase
      .from("it_notifications")
      .select("id, kind, title, body, case_id, read_at, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20),
  ]);

  if (userRes.error) throw userRes.error;
  if (!userRes.data) return null;

  return {
    user: userRes.data,
    children: childrenRes.data ?? [],
    cases: (casesRes.data ?? []).map((c) => ({
      ...c,
      child: Array.isArray(c.child) ? c.child[0] : c.child,
    })),
    purchases: purchasesRes.data ?? [],
    credits: creditsRes.data ?? [],
    notifications: notifsRes.data ?? [],
  };
}

// 발행 완료된 케이스의 알림을 보호자에게 다시 보낸다. 보호자가 알림을
// 지웠거나 못 봤다고 할 때 운영자가 회고 화면에서 누른다.
// it_notifications에 새 row를 insert. 기존 'report_ready'와 동일 kind를
// 쓰되 body 문구를 "다시 안내" 톤으로 바꾼다 — 부모 UI가 kind만 보고
// 분기하지 않게.
export async function resendReportNotification({ caseId }) {
  if (!caseId) throw new Error("case_id가 필요해요");

  const { data: kase, error: cErr } = await supabase
    .from("it_cases")
    .select(`
      parent_id, concern_tags, status,
      child:it_children(name)
    `)
    .eq("id", caseId)
    .maybeSingle();
  if (cErr) throw cErr;
  if (!kase) throw new Error("케이스를 찾을 수 없어요");
  if (kase.status !== "done") throw new Error("발행 완료된 케이스만 재알림을 보낼 수 있어요");
  if (!kase.parent_id) throw new Error("보호자 정보가 없어요");

  const child = Array.isArray(kase.child) ? kase.child[0] : kase.child;
  const childName = child?.name ?? "아이";
  const tag = kase.concern_tags?.[0];

  const { error: insErr } = await supabase.from("it_notifications").insert({
    user_id: kase.parent_id,
    kind: "report_ready",
    title: `${childName} 리포트 다시 안내드려요`,
    body: tag
      ? `${tag} 관찰 리포트가 그대로 남아 있어요. 앱에서 다시 열어볼 수 있어요.`
      : "분석 리포트가 그대로 남아 있어요. 앱에서 다시 열어볼 수 있어요.",
    case_id: caseId,
  });
  if (insErr) throw insErr;
  return { ok: true };
}

// 현재 케이스(작업 중이든 회고 중이든)와 비슷한 과거 발행 케이스를 점수
// 매겨 상위 N개 반환. 점수는 단순 가중 합:
//   - 공통 concern_tag 1개 +2 (보호자가 입력한 큰 카테고리, 가중치 높음)
//   - 공통 symptom key 1개 +1 (운영진이 체크한 세부 증상)
// 자기 자신·draft·cancelled는 제외. status='done'만 본다.
//
// 발행 케이스가 수백 건 안 되는 동안엔 최근 200건 가져와 클라이언트 후처리.
// 더 쌓이면 Postgres RPC로 옮길 것.
export async function findSimilarCases({ caseId, limit = 5 } = {}) {
  if (!caseId) return [];

  // 1) 현재 케이스의 concern_tags + symptoms 조회
  const [meRes, meObsRes] = await Promise.all([
    supabase
      .from("it_cases")
      .select("id, concern_tags, child_id")
      .eq("id", caseId)
      .maybeSingle(),
    supabase
      .from("it_observations")
      .select("symptoms")
      .eq("case_id", caseId)
      .maybeSingle(),
  ]);
  if (meRes.error) throw meRes.error;
  if (!meRes.data) return [];

  const myTags = new Set(meRes.data.concern_tags ?? []);
  const mySymptoms = new Set(
    Object.entries((meObsRes.data?.symptoms ?? {}))
      .filter(([, v]) => v)
      .map(([k]) => k),
  );

  // 매칭 요소가 하나도 없으면 비교 불가능
  if (myTags.size === 0 && mySymptoms.size === 0) return [];

  // 2) 발행 완료 후보군 — 최근 200건 (사이즈가 커지면 RPC로)
  const { data, error } = await supabase
    .from("it_cases")
    .select(`
      id, concern_tags, sent_at,
      child:it_children(name, birth_month),
      observations:it_observations(symptoms)
    `)
    .eq("status", "done")
    .neq("id", caseId)
    .order("sent_at", { ascending: false, nullsFirst: false })
    .limit(200);
  if (error) throw error;

  // 3) 점수 계산
  const scored = (data ?? []).map((c) => {
    const child = Array.isArray(c.child) ? c.child[0] : c.child;
    const obs = Array.isArray(c.observations) ? c.observations[0] : c.observations;
    const tags = c.concern_tags ?? [];
    const symptoms = Object.entries((obs?.symptoms ?? {}))
      .filter(([, v]) => v)
      .map(([k]) => k);

    let score = 0;
    const commonTags = [];
    for (const t of tags) {
      if (myTags.has(t)) { score += 2; commonTags.push(t); }
    }
    const commonSymptoms = [];
    for (const s of symptoms) {
      if (mySymptoms.has(s)) { score += 1; commonSymptoms.push(s); }
    }

    return {
      id: c.id,
      child,
      sent_at: c.sent_at,
      score,
      commonTags,
      commonSymptoms,
    };
  });

  // 4) 점수 > 0만, 점수 desc → sent_at desc로 정렬해서 상위 limit
  return scored
    .filter((r) => r.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aT = a.sent_at ? new Date(a.sent_at).getTime() : 0;
      const bT = b.sent_at ? new Date(b.sent_at).getTime() : 0;
      return bT - aT;
    })
    .slice(0, limit);
}

// 단일 케이스의 발행된 리포트 + 운영진 회고에 필요한 모든 부속 정보.
// 한 번의 호출로 운영진 시점 패널과 부모 시점 ReportView를 동시에 그릴 수
// 있게 모양을 다듬어서 돌려준다.
export async function getArchiveReport(caseId) {
  const { data: caseRow, error: cErr } = await supabase
    .from("it_cases")
    .select(`
      id, concern_tags, concern_memo, parent_id, plan_tier, ai_model,
      submitted_at, sent_at, created_at,
      child:it_children(id, name, birth_month, avatar_url),
      parent:it_users!it_cases_parent_id_fkey(id, email, display_name),
      videos:it_videos(id, position, duration_seconds, storage_path, thumbnail_path, mime_type)
    `)
    .eq("id", caseId)
    .maybeSingle();
  if (cErr) throw cErr;
  if (!caseRow) return null;

  // 부모가 본 최종 리포트 + 짱샘 관찰 메모/체크리스트 + AI 토큰 사용량을
  // 병렬로 가져온다.
  const [reportRes, obsRes, researchRes] = await Promise.all([
    supabase
      .from("it_reports")
      .select("case_id, letter, content, finalized_at, viewed_at")
      .eq("case_id", caseId)
      .maybeSingle(),
    supabase
      .from("it_observations")
      .select("symptoms, notes, updated_at")
      .eq("case_id", caseId)
      .maybeSingle(),
    supabase
      .from("it_research")
      .select("model_used, tokens_input, tokens_output, completed_at, status, error_message")
      .eq("case_id", caseId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  if (reportRes.error) throw reportRes.error;

  const child = Array.isArray(caseRow.child) ? caseRow.child[0] : caseRow.child;
  const parent = Array.isArray(caseRow.parent) ? caseRow.parent[0] : caseRow.parent;
  const videos = (caseRow.videos ?? []).sort((a, b) => a.position - b.position);

  return {
    // ReportView 시그니처에 맞춘 객체 — phone-frame 안에서 그대로 사용.
    caseRecord: {
      id: caseRow.id,
      concern_tags: caseRow.concern_tags ?? [],
      it_children: child ? { name: child.name } : null,
    },
    report: reportRes.data ?? null,
    // 운영진 회고용 부속 데이터
    meta: {
      child,
      parent,
      submitted_at: caseRow.submitted_at,
      sent_at: caseRow.sent_at,
      created_at: caseRow.created_at,
      concern_memo: caseRow.concern_memo,
      plan_tier: caseRow.plan_tier,
      ai_model: caseRow.ai_model,
    },
    videos,
    observations: obsRes.data ?? null,
    research: researchRes.data ?? null,
  };
}

// ────────────────────────────────────────────────────────────────────
// Case detail (workspace)
// ────────────────────────────────────────────────────────────────────

export async function getAdminCaseDetail(caseId) {
  const { data: caseData, error: caseErr } = await supabase
    .from("it_cases")
    .select(`
      *,
      child:it_children(id, name, birth_month, avatar_url),
      videos:it_videos(id, position, duration_seconds, storage_path, thumbnail_path, mime_type)
    `)
    .eq("id", caseId)
    .maybeSingle();
  if (caseErr) throw caseErr;
  if (!caseData) return null;

  const { data: obs } = await supabase
    .from("it_observations")
    .select("*")
    .eq("case_id", caseId)
    .maybeSingle();

  const { data: research } = await supabase
    .from("it_research")
    .select("*")
    .eq("case_id", caseId)
    .order("created_at", { ascending: false })
    .limit(1);

  return {
    ...caseData,
    videos: (caseData.videos ?? []).sort((a, b) => a.position - b.position),
    observations: obs ?? null,
    research: research?.[0] ?? null,
  };
}

export async function getSignedVideoUrl(storagePath, expiresIn = 3600) {
  const { data, error } = await supabase.storage
    .from("it-videos")
    .createSignedUrl(storagePath, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}

// ────────────────────────────────────────────────────────────────────
// Thumbnail backfill — for videos uploaded before thumbnail_path existed
// ────────────────────────────────────────────────────────────────────

// Perceived-luminance check — phone clips often fade in / auto-expose, so the
// first frame is frequently black even when the clip has content. getImageData
// throws on a CORS-tainted canvas; there we can't measure → treat as not dark.
function frameIsTooDark(ctx, w, h) {
  try {
    const { data } = ctx.getImageData(0, 0, w, h);
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return sum / (data.length / 4) < 18; // 0..255 — <18 is essentially black
  } catch {
    return false;
  }
}

// Candidate capture timestamps in priority order — skip the (often black) start.
function pickThumbTimes(duration) {
  if (Number.isFinite(duration) && duration > 0) {
    const cap = Math.max(duration - 0.1, 0.1);
    return [
      Math.min(Math.max(duration * 0.1, 1.0), cap),
      Math.min(duration * 0.4, cap),
      Math.min(duration * 0.7, cap),
      0.1,
    ];
  }
  return [1.0, 2.5, 0.3];
}

// Extract a single JPEG dataURL from a video URL using a hidden <video>.
// Mirrors probeVideoMeta() in cases.js but works against a remote URL with
// CORS (Supabase signed URLs return Access-Control-Allow-Origin: *). Seeks a
// little way in and retries on black frames so backfilled thumbs aren't black.
function extractThumbnailFromUrl(url) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.preload = "auto";
    v.muted = true;
    v.playsInline = true;
    v.style.cssText =
      "position:fixed;left:0;top:0;width:4px;height:4px;opacity:0.01;" +
      "pointer-events:none;z-index:-9999;";
    if (typeof document !== "undefined" && document.body) {
      document.body.appendChild(v);
    }
    let settled = false;
    let result = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      try { v.pause?.(); } catch { /* noop */ }
      try { v.removeAttribute("src"); v.load?.(); } catch { /* noop */ }
      try { v.remove(); } catch { /* noop */ }
      resolve(result);
    };

    let times = [];
    let idx = 0;
    const grabAt = () => {
      if (settled) return;
      if (idx >= times.length) { finish(); return; }
      const onFrameReady = () => {
        let fired = false;
        const fire = () => {
          if (fired || settled) return;
          fired = true;
          if (!v.videoWidth || !v.videoHeight) {
            console.warn("thumb extract: no dimensions", v.videoWidth, v.videoHeight);
            finish(); return;
          }
          const w = 240;
          const h = Math.round(w * (v.videoHeight / v.videoWidth));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) { finish(); return; }
          try {
            ctx.drawImage(v, 0, 0, w, h);
            const lastTry = idx >= times.length - 1;
            if (!lastTry && frameIsTooDark(ctx, w, h)) {
              idx += 1;
              grabAt();
              return;
            }
            result = canvas.toDataURL("image/jpeg", 0.7);
          } catch (e) { console.warn("thumb extract: grab failed", e); }
          finish();
        };
        if (typeof v.requestVideoFrameCallback === "function") {
          try { v.requestVideoFrameCallback(fire); } catch { /* noop */ }
        }
        requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(fire, 80)));
      };
      v.onseeked = onFrameReady;
      try { v.currentTime = times[idx]; } catch { finish(); }
    };

    v.onloadeddata = () => {
      console.info("thumb extract: loadeddata, readyState=%d, %dx%d", v.readyState, v.videoWidth, v.videoHeight);
      times = pickThumbTimes(Number.isFinite(v.duration) ? v.duration : null);
      idx = 0;
      const p = v.play?.();
      if (p && typeof p.then === "function") {
        p.then(() => { try { v.pause(); } catch { /* noop */ } grabAt(); })
          .catch((e) => { console.warn("thumb extract: play() rejected", e?.message); grabAt(); });
      } else {
        grabAt();
      }
    };
    v.onerror = () => { console.warn("thumb extract: video load error"); finish(); };
    setTimeout(() => { console.warn("thumb extract: timeout, readyState=%d", v.readyState); finish(); }, 20000);
    console.info("thumb extract: loading video...");
    v.src = url;
  });
}

// Generate + upload a thumbnail for a video that has no thumbnail_path,
// then patch the row. Returns the freshly signed URL on success.
export async function backfillVideoThumbnail(video) {
  if (!video?.storage_path || !video?.id) {
    console.warn("thumb backfill: missing storage_path or id", video?.id);
    return null;
  }
  const videoUrl = await getSignedVideoUrl(video.storage_path);
  const dataUrl = await extractThumbnailFromUrl(videoUrl);
  if (!dataUrl) {
    console.warn("thumb backfill: extractThumbnailFromUrl returned null for", video.id);
    return null;
  }
  const blob = await (await fetch(dataUrl)).blob();
  const tPath = `${video.storage_path}.thumb.jpg`;
  const { error: upErr } = await supabase.storage
    .from("it-videos")
    .upload(tPath, blob, {
      cacheControl: "86400",
      upsert: true,
      contentType: "image/jpeg",
    });
  if (upErr) return null;
  const { error: dbErr } = await supabase
    .from("it_videos")
    .update({ thumbnail_path: tPath })
    .eq("id", video.id);
  if (dbErr) return null;
  return await getSignedVideoUrl(tPath);
}

// ────────────────────────────────────────────────────────────────────
// Observations save
// ────────────────────────────────────────────────────────────────────

export async function upsertObservations({ caseId, symptoms, notes, marks }) {
  const { data, error } = await supabase
    .from("it_observations")
    .upsert(
      {
        case_id: caseId,
        symptoms: symptoms ?? {},
        notes: notes ?? null,
        marks: marks ?? [],
      },
      { onConflict: "case_id" }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ────────────────────────────────────────────────────────────────────
// Workflow transitions
// ────────────────────────────────────────────────────────────────────

export async function markCaseAnalyzing({ caseId, operatorId }) {
  // No-op if already analyzing or beyond; idempotent move from 'received'
  const { data: current } = await supabase
    .from("it_cases")
    .select("status, operator_id")
    .eq("id", caseId)
    .maybeSingle();
  if (!current) return;
  if (current.status !== "received") return;

  await supabase
    .from("it_cases")
    .update({
      status: "analyzing",
      operator_id: operatorId ?? current.operator_id ?? null,
    })
    .eq("id", caseId);
}

// Real research — POSTs to /api/research-case (Vercel Serverless Function)
// which calls Claude (Haiku 4.5 for trial/basic, Opus 4.7 for pro), writes
// it_research, and flips the case to 'reviewing'. On failure the server
// rolls case back to 'analyzing'.
export async function runResearch({ caseId }) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("로그인이 필요해요");

  const response = await fetch("/api/research-case", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ case_id: caseId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(body.error ?? `HTTP ${response.status}`);
  }

  return await response.json();
}

// 인포그래픽만 따로 재생성. letter·논문·권유는 보존하고 Gemini만 다시 굴린다.
export async function regenerateIllustrations({ caseId }) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("로그인이 필요해요");

  const response = await fetch("/api/regenerate-illustrations", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ case_id: caseId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(body.error ?? `HTTP ${response.status}`);
  }

  return await response.json();
}

// ────────────────────────────────────────────────────────────────────
// Refunds (operator-only)
// ────────────────────────────────────────────────────────────────────

export async function listRefundablePurchases({ limit = 100 } = {}) {
  // Pull pkg-type purchases with their linked credit package and user.
  const { data, error } = await supabase
    .from("it_purchases")
    .select(`
      id, user_id, plan_type, amount_won, status,
      paid_at, refunded_at, refund_reason, payment_provider, payment_ref,
      user:it_users(email, display_name),
      package:it_credit_packages(id, credits_total, credits_remaining)
    `)
    .like("plan_type", "pkg%")
    .in("status", ["paid", "refunded"])
    .order("paid_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((p) => {
    const pkg = Array.isArray(p.package) ? p.package[0] : p.package;
    const user = Array.isArray(p.user) ? p.user[0] : p.user;
    return {
      ...p,
      user,
      package: pkg ?? null,
      credits_total: pkg?.credits_total ?? null,
      credits_remaining: pkg?.credits_remaining ?? null,
      credits_used: pkg ? pkg.credits_total - pkg.credits_remaining : null,
    };
  });
}

export async function refundPurchase({ purchaseId, reason, force = false }) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("로그인이 필요해요");

  const response = await fetch("/api/refund-payment", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      purchase_id: purchaseId,
      reason: reason ?? null,
      force,
    }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(body.error ?? `HTTP ${response.status}`);
    err.requiresForce = body.requires_force === true;
    err.used = body.used ?? null;
    err.total = body.total ?? null;
    err.code = body.code ?? null;
    throw err;
  }
  return body;
}

// ────────────────────────────────────────────────────────────────────
// Free grants — 이메일 사전 등록 → 가입 시 무료 분석권 자동 지급
// ────────────────────────────────────────────────────────────────────

async function freeGrantFetch(method, { body, query } = {}) {
  const qs = query
    ? "?" + new URLSearchParams(query).toString()
    : "";

  const callOnce = async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) throw new Error("로그인이 필요해요");
    return await fetch(`/api/free-grant${qs}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  };

  let res = await callOnce();
  // 만료된 access_token을 잡고 한 번 리프레시 후 재시도
  if (res.status === 401) {
    await supabase.auth.refreshSession().catch(() => {});
    res = await callOnce();
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return json;
}

export async function listFreeGrants() {
  const json = await freeGrantFetch("GET");
  return json.grants ?? [];
}

export async function createFreeGrant({ email, credits, expiresMonths, note, childName, parentName }) {
  return await freeGrantFetch("POST", {
    body: {
      email,
      credits,
      expires_months: expiresMonths,
      note,
      child_name: childName,
      parent_name: parentName,
    },
  });
}

export async function updateFreeGrant({ id, childName, parentName }) {
  return await freeGrantFetch("PATCH", {
    body: { id, child_name: childName, parent_name: parentName },
  });
}

export async function deleteFreeGrant(id) {
  return await freeGrantFetch("DELETE", { query: { id } });
}

// ────────────────────────────────────────────────────────────────────
// Finalize — operator approves, report is sent to the parent
// ────────────────────────────────────────────────────────────────────

export async function finalizeReport({ caseId, operatorId, letter, content }) {
  const now = new Date().toISOString();

  const { error: reportErr } = await supabase
    .from("it_reports")
    .upsert(
      {
        case_id: caseId,
        letter,
        content: content ?? {},
        finalized_by: operatorId,
        finalized_at: now,
      },
      { onConflict: "case_id" }
    );
  if (reportErr) throw reportErr;

  const { error: caseErr } = await supabase
    .from("it_cases")
    .update({ status: "done", sent_at: now })
    .eq("id", caseId);
  if (caseErr) throw caseErr;

  // Notify the parent. Policy: operator-only insert.
  const { data: kase } = await supabase
    .from("it_cases")
    .select("parent_id, concern_tags, child:it_children(name)")
    .eq("id", caseId)
    .maybeSingle();

  if (kase?.parent_id) {
    await supabase.from("it_notifications").insert({
      user_id: kase.parent_id,
      kind: "report_ready",
      title: `${kase.child?.name ?? "아이"} 리포트가 도착했어요`,
      body: kase.concern_tags?.[0]
        ? `${kase.concern_tags[0]} 관찰 리포트가 준비됐어요.`
        : "분석 리포트가 준비됐어요.",
      case_id: caseId,
    });
  }

  // 이메일 + 알림톡 — fire-and-forget (실패해도 전송 자체는 완료)
  sendReportEmail({ caseId }).catch(() => {});
  sendReportAlimtalk({ caseId }).catch(() => {});
}

export async function sendReportEmail({ caseId }) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return;

  const response = await fetch("/api/notify-report-email", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ case_id: caseId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${response.status}`);
  }

  return await response.json();
}

export async function sendReportAlimtalk({ caseId }) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return;

  const response = await fetch("/api/notify-report-alimtalk", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ case_id: caseId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${response.status}`);
  }

  return await response.json();
}
